import { Analytics, setAnalyticsContext } from '@inboundx/shared/analytics';
import { fetchClientConfigs, fetchDomain } from '@inboundx/shared/config';
import { logger } from '@inboundx/shared/platform';
import { AlertCircle, ArrowLeft, CheckCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

interface FormData {
    [key: string]: string;
}

export default function FormTestPage() {
    const { formType } = useParams<{ formType: string }>();
    const [formData, setFormData] = useState<FormData>({});
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Set up analytics context for form tracking
    useEffect(() => {
        // This context will be used by the form tracking system
        setAnalyticsContext({
            domain: 'localhost', // Use localhost for testing - should match site config
            sessionId: `form-test-${Date.now()}`,
            apiVersion: '1.0.0',
            frontendVersion: '1.0.0',
            deploymentTarget: 'preprod',
            environment: 'development',
        });

        logger.debug('🎯 Form Test Page - Analytics context set for form tracking');
        logger.debug('📄 Current URL:', window.location.href);
        logger.debug(
            '🔧 Form listeners should auto-setup if this URL matches CTA URLs in site config'
        );

        // Debug: Check if domain config exists for localhost
        const checkSiteConfig = async () => {
            try {
                const domainRow = await fetchDomain('localhost');
                logger.debug('🔍 DEBUG: Domain row for localhost:', domainRow);

                if (domainRow) {
                    const clientConfigs = await fetchClientConfigs('localhost');
                    logger.debug(
                        '🔍 DEBUG: Client configs:',
                        JSON.stringify(clientConfigs, null, 2)
                    );

                    const ctasConfig = clientConfigs.find((c) => c.config_slug === 'ctas');
                    const ctas = (ctasConfig?.config as Record<string, unknown>)?.ctas;
                    if (Array.isArray(ctas)) {
                        const urls: string[] = [];
                        ctas.forEach((cta: { url?: Record<string, string> }) => {
                            if (cta.url) {
                                Object.values(cta.url).forEach((u: string) => {
                                    urls.push(u);
                                });
                            }
                        });

                        logger.debug('🎯 DEBUG: CTA URLs found:', urls);

                        const currentUrl = window.location.href;
                        const matches = urls.filter(
                            (url) => currentUrl.includes(url) || url.includes(currentUrl)
                        );

                        logger.debug('📍 DEBUG: URL matches:', matches);
                        logger.debug('📍 DEBUG: Should setup form tracking:', matches.length > 0);
                    } else {
                        logger.debug('❌ DEBUG: No CTAs found in config');
                    }
                } else {
                    logger.debug('❌ DEBUG: Domain "localhost" not found in unified config');
                }
            } catch (error) {
                logger.error('❌ DEBUG: Error checking site config:', error);
            }
        };

        checkSiteConfig();

        // Debug: Check if form listeners are actually being set up
        const checkFormListeners = () => {
            const forms = document.querySelectorAll('form');
            logger.debug('🔍 DEBUG: Forms found on page:', forms.length);

            forms.forEach((form, index) => {
                const hasListener = (form as HTMLFormElement & { __ctaFormTracked?: boolean })
                    .__ctaFormTracked;
                logger.debug(`📝 DEBUG: Form ${index + 1} has CTA listener:`, hasListener);
                logger.debug(`📝 DEBUG: Form ${index + 1} details:`, {
                    id: form.id,
                    className: form.className,
                    action: form.action,
                    method: form.method,
                });
            });
        };

        // Check after a delay to allow form setup
        const timeoutId = setTimeout(checkFormListeners, 2000);

        // Cleanup timeout on unmount to prevent memory leaks
        return () => {
            clearTimeout(timeoutId);
        };
    }, []);

    const handleInputChange = (field: string, value: string) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        const formConfig = getFormConfig(formType);

        if (formConfig.useAjax) {
            // For AJAX forms - don't prevent default, let button click tracking handle it
            logger.debug(
                '🌐 AJAX form submission - letting automatic button click tracking handle analytics'
            );

            // Simulate AJAX submission without preventing default
            setIsSubmitting(true);

            // Simulate network request delay
            await new Promise((resolve) => setTimeout(resolve, 1500));

            setIsSubmitted(true);
            setIsSubmitting(false);

            logger.debug(
                '✅ AJAX form submission completed - button click tracking should have fired'
            );
            return;
        }

        // Traditional form handling with preventDefault
        event.preventDefault();
        setIsSubmitting(true);

        logger.debug('📝 Traditional form submission started:', { formType, formData });

        // Since preventDefault() blocks the automatic form listener,
        // we need to manually trigger the form tracking
        const form = event.currentTarget;
        const fieldCount = form.querySelectorAll('input, select, textarea').length;

        await Analytics.trackDemoFormSubmitted({
            formId: form.id || 'unknown_form',
            formClass: form.className || '',
            pageUrl: window.location.href,
            formAction: form.action || '',
            formMethod: form.method || 'POST',
            fieldCount,
            detectionMethod: 'manual_tracking',
            status: 'unknown' as const,
        });

        logger.debug(
            '📊 Form analytics tracked manually (automatic listener blocked by preventDefault)'
        );

        // Simulate form submission delay
        await new Promise((resolve) => setTimeout(resolve, 1000));

        setIsSubmitted(true);
        setIsSubmitting(false);

        logger.debug('✅ Traditional form submission completed - analytics tracked manually');
    };

    const getFormConfig = (type: string | undefined) => {
        switch (type) {
            case 'demo':
                return {
                    title: 'Request Demo',
                    description: 'Schedule a personalized demo of our platform',
                    trackingMethod: 'Traditional form submission + button click detection',
                    fields: [
                        { id: 'firstName', label: 'First Name', type: 'text', required: true },
                        { id: 'lastName', label: 'Last Name', type: 'text', required: true },
                        { id: 'email', label: 'Email Address', type: 'email', required: true },
                        { id: 'company', label: 'Company', type: 'text', required: true },
                        { id: 'jobTitle', label: 'Job Title', type: 'text', required: false },
                        { id: 'phone', label: 'Phone Number', type: 'tel', required: false },
                        {
                            id: 'message',
                            label: 'Tell us about your needs',
                            type: 'textarea',
                            required: false,
                        },
                    ],
                };
            case 'contact':
                return {
                    title: 'Contact Us',
                    description: 'Get in touch with our team (uses preventDefault)',
                    trackingMethod: 'Manual tracking due to preventDefault()',
                    fields: [
                        { id: 'name', label: 'Full Name', type: 'text', required: true },
                        { id: 'email', label: 'Email Address', type: 'email', required: true },
                        { id: 'subject', label: 'Subject', type: 'text', required: true },
                        { id: 'message', label: 'Message', type: 'textarea', required: true },
                    ],
                };
            case 'ajax':
                return {
                    title: 'AJAX Form Test',
                    description: 'Tests new button click detection for AJAX submissions',
                    trackingMethod: 'Button click detection (Method 2)',
                    useAjax: true,
                    fields: [
                        { id: 'name', label: 'Full Name', type: 'text', required: true },
                        { id: 'email', label: 'Email Address', type: 'email', required: true },
                        { id: 'company', label: 'Company', type: 'text', required: false },
                    ],
                };
            case 'mayday':
                return {
                    title: 'Mayday-Style Form',
                    description: 'Simulates the Mayday form behavior with JavaScript submission',
                    trackingMethod: 'Button click detection (simulates real Mayday behavior)',
                    useAjax: true,
                    maydayStyle: true,
                    fields: [
                        { id: 'firstName', label: 'First name', type: 'text', required: true },
                        { id: 'name', label: 'Name', type: 'text', required: true },
                        { id: 'email', label: 'Professional email', type: 'email', required: true },
                        { id: 'phone', label: 'Phone number', type: 'tel', required: true },
                        { id: 'position', label: 'Position', type: 'text', required: false },
                        {
                            id: 'representatives',
                            label: 'Number of customer service representatives',
                            type: 'text',
                            required: false,
                        },
                        {
                            id: 'comments',
                            label: 'Additional comments',
                            type: 'textarea',
                            required: false,
                        },
                    ],
                };
            default:
                return {
                    title: 'Test Form',
                    description: 'Generic test form for analytics tracking',
                    trackingMethod: 'Standard detection',
                    fields: [
                        { id: 'name', label: 'Name', type: 'text', required: true },
                        { id: 'email', label: 'Email', type: 'email', required: true },
                    ],
                };
        }
    };

    const formConfig = getFormConfig(formType);

    if (isSubmitted) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 p-8">
                <div className="max-w-2xl mx-auto">
                    <div className="text-center">
                        <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-6" />
                        <h1 className="text-3xl font-bold text-gray-900 mb-4">
                            Form Submitted Successfully!
                        </h1>
                        <p className="text-lg text-gray-600 mb-8">
                            Your form has been submitted and analytics tracking should be complete.
                        </p>

                        <Card className="p-6 bg-green-50 border-green-200 mb-8">
                            <h3 className="text-lg font-semibold text-green-900 mb-4">
                                📊 Analytics Tracking Completed
                            </h3>
                            <div className="space-y-2 text-sm text-green-800 text-left">
                                <div>
                                    ✅ <strong>Event Tracked:</strong>{' '}
                                    <code>rw_client_form_submitted</code>
                                </div>
                                <div>
                                    ✅ <strong>Form ID:</strong>{' '}
                                    <code>{formType || 'unknown'}_form</code>
                                </div>
                                <div>
                                    ✅ <strong>Field Count:</strong> {formConfig.fields.length}{' '}
                                    fields
                                </div>
                                <div>
                                    ✅ <strong>Page URL:</strong>{' '}
                                    <code>{window.location.href}</code>
                                </div>
                                <div>
                                    ✅ <strong>Tracking Method:</strong> {formConfig.trackingMethod}
                                </div>
                                <div>
                                    ✅ <strong>Detection Method:</strong>{' '}
                                    <code>
                                        {formConfig.useAjax ? 'button_click' : 'manual_tracking'}
                                    </code>
                                </div>
                                <div>
                                    ✅ <strong>Funnel Step:</strong> 8 (after CTA click)
                                </div>
                            </div>
                        </Card>

                        <div className="space-y-4">
                            <Link to="/cta-test">
                                <Button className="w-full bg-blue-600 hover:bg-blue-700">
                                    <ArrowLeft className="mr-2 h-4 w-4" />
                                    Back to CTA Test Page
                                </Button>
                            </Link>
                            <Link to="/">
                                <Button variant="outline" className="w-full">
                                    Back to Main App
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-100 p-8">
            <div className="max-w-2xl mx-auto">
                {/* Header */}
                <div className="text-center mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">{formConfig.title}</h1>
                    <p className="text-lg text-gray-600 mb-6">{formConfig.description}</p>
                </div>

                {/* Navigation */}
                <div className="mb-6">
                    <Link
                        to="/cta-test"
                        className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors"
                    >
                        <ArrowLeft className="h-4 w-4 mr-1" />
                        Back to CTA Test Page
                    </Link>
                </div>

                {/* Analytics Info */}
                <Card className="p-4 bg-blue-50 border-blue-200 mb-6">
                    <div className="flex items-start">
                        <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
                        <div className="text-sm text-blue-800">
                            <div className="mb-2">
                                <strong>Form Tracking Active:</strong> This page tracks form
                                submissions as <code>rw_client_form_submitted</code> events.
                            </div>
                            <div className="mb-2">
                                <strong>Tracking Method:</strong> {formConfig.trackingMethod}
                            </div>
                            {formConfig.useAjax && (
                                <div className="text-xs text-blue-600 bg-blue-100 p-2 rounded mt-2">
                                    🚀 <strong>AJAX Test:</strong> This form simulates modern
                                    JavaScript submission behavior. The new button click detection
                                    should capture the submission automatically.
                                </div>
                            )}
                        </div>
                    </div>
                </Card>

                {/* Form */}
                <Card className="p-8">
                    <form
                        onSubmit={handleSubmit}
                        id={`${formType || 'test'}_form`}
                        className="space-y-6"
                        action="#"
                        method="POST"
                    >
                        {formConfig.fields.map((field) => (
                            <div key={field.id}>
                                <label
                                    htmlFor={field.id}
                                    className="block text-sm font-medium text-gray-700 mb-2"
                                >
                                    {field.label}
                                    {field.required && <span className="text-red-500 ml-1">*</span>}
                                </label>

                                {field.type === 'textarea' ? (
                                    <textarea
                                        id={field.id}
                                        name={field.id}
                                        required={field.required}
                                        rows={4}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        value={formData[field.id] || ''}
                                        onChange={(e) =>
                                            handleInputChange(field.id, e.target.value)
                                        }
                                        placeholder={`Enter your ${field.label.toLowerCase()}`}
                                    />
                                ) : (
                                    <input
                                        type={field.type}
                                        id={field.id}
                                        name={field.id}
                                        required={field.required}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        value={formData[field.id] || ''}
                                        onChange={(e) =>
                                            handleInputChange(field.id, e.target.value)
                                        }
                                        placeholder={`Enter your ${field.label.toLowerCase()}`}
                                    />
                                )}
                            </div>
                        ))}

                        <Button
                            type="submit"
                            className="w-full bg-indigo-600 hover:bg-indigo-700"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? 'Submitting...' : 'Submit Form'}
                        </Button>
                    </form>
                </Card>

                {/* Debug Info */}
                <Card className="mt-6 p-4 bg-gray-50 border-gray-200">
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">Debug Information</h3>
                    <div className="space-y-1 text-xs text-gray-600">
                        <div>
                            <strong>Form Type:</strong> {formType || 'default'}
                        </div>
                        <div>
                            <strong>Form ID:</strong> <code>{formType || 'test'}_form</code>
                        </div>
                        <div>
                            <strong>Field Count:</strong> {formConfig.fields.length}
                        </div>
                        <div>
                            <strong>Current URL:</strong> <code>{window.location.href}</code>
                        </div>
                        <div>
                            <strong>Expected Event:</strong> <code>rw_client_form_submitted</code>
                        </div>
                    </div>
                </Card>
            </div>
        </div>
    );
}
